import {
  Button,
  Flex,
  FormControl,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Country,
  countriesApi,
} from '@texas/api/endpoints/metadata/countriesApi';
import { Currency, currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { VatCode, vatCodesApi } from '@texas/api/endpoints/vatCodesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import {
  BranchValue,
  BranchValueKind,
  BranchValueModal,
} from './BranchValueModal';
import {
  getConfigurationBranchId,
  setConfigurationBranchId,
} from '../configurationBranch';

interface BranchValuesProps {
  kind: BranchValueKind;
}

const titles: Record<BranchValueKind, string> = {
  countries: 'configuration.navigation.countries',
  currencies: 'configuration.navigation.currencies',
  vatCodes: 'configuration.navigation.vatCodes',
};

async function getValues(
  kind: BranchValueKind,
  branchId: number,
): Promise<HttpClientResponse<BranchValue[]>> {
  let response: HttpClientResponse<Country[] | Currency[] | VatCode[]>;
  switch (kind) {
    case 'countries':
      response = await countriesApi.getCountries(branchId);
      break;
    case 'currencies':
      response = await currenciesApi.getForBranch(branchId);
      break;
    case 'vatCodes':
      response = await vatCodesApi.getForBranch(branchId);
      break;
  }
  return response as HttpClientResponse<BranchValue[]>;
}

export function BranchValues({ kind }: BranchValuesProps) {
  const { t } = useTranslation();
  const { branches } = useActiveSession();
  const [branchId, setBranchId] = useState(() =>
    getConfigurationBranchId(branches),
  );
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<BranchValue | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(getValues);
  const title = t(titles[kind] as 'configuration.navigation.garp');

  useEffect(() => {
    if (branchId) refetch(kind, branchId);
  }, [branchId, kind, refetch]);

  const values = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((item) => !item.archived)
      .filter((item) =>
        Object.values(item).some(
          (value) =>
            typeof value === 'string' &&
            value.toLowerCase().includes(normalized),
        ),
      );
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={title}
      toolbar={
        <>
          <FormControl w="240px">
            <Select
              aria-label={t('general.branch')}
              h="44px"
              variant="entityDirectory"
              value={branchId}
              onChange={(event) => {
                const id = Number(event.target.value);
                setConfigurationBranchId(id);
                setBranchId(id);
              }}
            >
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <SearchFilterInput
            value={filter}
            style="entityDirectory"
            onChange={setFilter}
          />
        </>
      }
      action={
        <Button
          variant="entityDirectoryPrimary"
          onClick={() => {
            setSelected(null);
            onOpen();
          }}
        >
          {t('general.new')} {title.toLowerCase()}
        </Button>
      }
    >
      <BranchValueModal
        branchId={branchId}
        isOpen={isOpen}
        kind={kind}
        onClose={onClose}
        onSaved={() => refetch(kind, branchId)}
        value={selected}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              {kind !== 'vatCodes' && <Th>{t('general.code')}</Th>}
              {kind === 'currencies' && (
                <>
                  <Th>{t('configuration.buyingRate')}</Th>
                  <Th>{t('configuration.sellingRate')}</Th>
                </>
              )}
              {kind === 'vatCodes' && (
                <>
                  <Th>{t('configuration.identifier')}</Th>
                  <Th>{t('general.value')}</Th>
                  <Th>{t('branch.displayOrder')}</Th>
                </>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {!loading && values.length === 0 && (
              <Tr>
                <Td colSpan={5}>{t('general.noData')}</Td>
              </Tr>
            )}
            {values.map((item) => (
              <Tr
                key={item.id}
                cursor="pointer"
                onClick={() => {
                  setSelected(item);
                  onOpen();
                }}
              >
                <Td>{item.name}</Td>
                {kind !== 'vatCodes' && (
                  <Td>
                    {'code' in item && typeof item.code === 'string'
                      ? item.code
                      : ''}
                  </Td>
                )}
                {kind === 'currencies' && (
                  <>
                    <Td>{'buyingRate' in item ? item.buyingRate : ''}</Td>
                    <Td>{'sellingRate' in item ? item.sellingRate : ''}</Td>
                  </>
                )}
                {kind === 'vatCodes' && (
                  <>
                    <Td>{'identifier' in item ? item.identifier : ''}</Td>
                    <Td>{'value' in item ? item.value : ''}</Td>
                    <Td>{'displayOrder' in item ? item.displayOrder : ''}</Td>
                  </>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
